import * as Yup from 'yup';
import { useMemo, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import v from 'voca';
// @mui
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
// routes
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { useLocales } from 'src/locales';
// components
import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFSwitch, RHFTextField, RHFEditor } from 'src/components/hook-form';
// hooks
import { createOverlay, updateOverlay } from 'src/api/admin';
// types
import { IOverlayItem } from 'src/types';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';

// ----------------------------------------------------------------------

type Props = {
  currentOverlay?: IOverlayItem;
  cloneOverlay?: IOverlayItem;
};

export default function OverlayNewEditForm({ currentOverlay, cloneOverlay }: Props) {
  const router = useRouter();
  const { enqueueSnackbar } = useSnackbar();

  const { t } = useLocales('overlayPage');

  const ProducerSchema = Yup.object().shape({
    title: Yup.string().required(`${t('title')} ${t('is_required')}`), // New title field validation
    content: Yup.string().required(`${t('content')} ${t('is_required')}`),
    startDate: Yup.date().required(`${t('startDate')} ${t('is_required')}`),
    endDate: Yup.date().required(`${t('endDate')} ${t('is_required')}`),
    active: Yup.boolean().required(`${t('active')} ${t('is_required')}`),
    isFallback: Yup.boolean().required(`${t('isFallback')} ${t('is_required')}`),
  });

  const defaultValues = useMemo(
    () => ({
      ...(currentOverlay || {}),
      content: currentOverlay?.content || cloneOverlay?.content || '',
      startDate: currentOverlay?.startDate || cloneOverlay?.startDate || null,
      endDate: currentOverlay?.endDate || cloneOverlay?.endDate || null,
      active: currentOverlay?.active || cloneOverlay?.active || false,
      isFallback: currentOverlay?.isFallback || cloneOverlay?.isFallback || false,
      title: currentOverlay?.title || '',
    }),
    [currentOverlay, cloneOverlay],
  );

  const methods = useForm({
    resolver: yupResolver<any, any, any>(ProducerSchema),
    defaultValues,
  });

  const {
    watch,
    reset,
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const values = watch();
  useEffect(() => {
    if (currentOverlay || cloneOverlay) {
      reset(defaultValues);
    }
  }, [currentOverlay, defaultValues, reset, cloneOverlay]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      let res = null;
      if (!currentOverlay) {
        res = await createOverlay(data);
        reset();
      } else {
        res = await updateOverlay(data);
      }
      enqueueSnackbar(currentOverlay ? `${t('update_success')}!` : `${t('create_success')}!`);
      if (res) {
        router.push(
          paths.admin.dashboard.overlays.details(data?.id ? data?.id : res?.data?.data?.id),
        );
      }
    } catch ({ message }) {
      if (message) {
        enqueueSnackbar(message, { variant: 'error' });
      }
    }
  });

  const renderDetails = (
    <Grid size={{ xs: 12 }}>
      <Card>
        <Stack spacing={3} sx={{ p: 3 }}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle2">{t('title')}</Typography>
            <RHFTextField name="title" label={t('title')} value={values?.title} />{' '}
            {/* New title field */}
          </Stack>

          <Stack spacing={1.5}>
            <Typography variant="subtitle2">{t('content')}</Typography>
            <RHFEditor name="content" />
          </Stack>

          <Stack spacing={1.5} gap={5} display="flex" flexDirection="row" alignItems="center">
            <Stack spacing={1.5}>
              <Typography variant="subtitle2">{t('valid_from')}</Typography>
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label={t('startDate')}
                    value={field.value ? dayjs(field.value) : null}
                    format="DD/MM/YYYY"
                    onChange={(newValue) => {
                      field.onChange(newValue ? newValue.toDate() : null);
                    }}
                    slotProps={{
                      textField: (params) => <RHFTextField {...params} name={field.name} />,
                    }}
                  />
                )}
              />
            </Stack>
            <Stack spacing={1.5}>
              <Typography variant="subtitle2">{t('valid_through')}</Typography>
              <Controller
                name="endDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label={t('endDate')}
                    value={field.value ? dayjs(field.value) : null}
                    format="DD/MM/YYYY"
                    onChange={(newValue) => {
                      field.onChange(newValue ? newValue.toDate() : null);
                    }}
                    slotProps={{
                      textField: (params) => <RHFTextField {...params} name={field.name} />,
                    }}
                  />
                )}
              />
            </Stack>
          </Stack>

          <RHFSwitch name="active" label={t('active')} />
          <RHFSwitch name="isFallback" disabled label={t('fallback')} />
        </Stack>
      </Card>
    </Grid>
  );

  const renderActions = (
    <Grid size={{ xs: 12 }} sx={{ display: 'flex', alignItems: 'center', mt: 3 }}>
      <LoadingButton
        type="submit"
        variant="contained"
        size="large"
        loading={isSubmitting}
        sx={{ ml: 2 }}
      >
        {!currentOverlay ? `${t('add')} ${v.lowerCase(t('overlay'))}` : t('save_changes')}
      </LoadingButton>
    </Grid>
  );

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Grid container spacing={3}>
        {renderDetails}
        {renderActions}
      </Grid>
    </FormProvider>
  );
}
